import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from '../ui/table';
  import { Button } from '../ui/button';
  import { Badge } from '../ui/badge';
  import { Input } from '../ui/input';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '../ui/dropdown-menu';
  import {
    MoreVertical,
    Eye,
    User,
    Store,
    Calendar,
    Clock,
    Search,
    Filter,
    Shield,
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
  } from 'lucide-react';
  import { AdminLog } from '../../lib/adminApi';
  import { format } from 'date-fns';
  import { es } from 'date-fns/locale';
  import { useState } from 'react';
  
  interface LogsTableProps {
    logs: AdminLog[];
    loading: boolean;
    onViewDetails: (log: AdminLog) => void;
    onSearch: (query: string) => void;
    onFilterChange: (filter: string) => void;
    currentFilter: string;
  }
  
  export function LogsTable({
    logs,
    loading,
    onViewDetails,
    onSearch,
    onFilterChange,
    currentFilter,
  }: LogsTableProps) {
    const [searchQuery, setSearchQuery] = useState('');
  
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      onSearch(value);
    };
  
    const formatDate = (dateString: string) => {
      try {
        return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss', { locale: es });
      } catch {
        return 'Fecha inválida';
      }
    };
  
    const getActionIcon = (actionType: string) => {
      switch (actionType) {
        case 'activate_account':
        case 'register_payment':
          return <CheckCircle className="h-4 w-4 mr-2 text-green-600" />;
        case 'suspend_account':
          return <XCircle className="h-4 w-4 mr-2 text-red-600" />;
        case 'extend_trial':
          return <Clock className="h-4 w-4 mr-2 text-yellow-600" />;
        case 'payment_receipt_uploaded':
          return <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />;
        default:
          return <Info className="h-4 w-4 mr-2 text-gray-600" />;
      }
    };
  
    const getActionText = (actionType: string) => {
      const actions: Record<string, string> = {
        'activate_account': 'Cuenta activada',
        'suspend_account': 'Cuenta suspendida',
        'extend_trial': 'Trial extendido',
        'register_payment': 'Pago registrado',
        'payment_receipt_uploaded': 'Comprobante subido',
        'trial_extension_request': 'Solicitud extensión trial',
        'shop_contact_request': 'Contacto de negocio',
        'login': 'Inicio de sesión',
        'logout': 'Cierre de sesión',
        'create_shop': 'Negocio creado',
        'update_shop': 'Negocio actualizado',
      };
      
      return actions[actionType] || actionType.replace(/_/g, ' ');
    };
  
    const getActionBadge = (actionType: string) => {
      const baseClass = "text-xs font-medium px-2 py-1 rounded-full";
      
      switch (actionType) {
        case 'activate_account':
        case 'register_payment':
          return <Badge className={`${baseClass} bg-green-100 text-green-800 border-green-200`}>
            {getActionText(actionType)}
          </Badge>;
        case 'suspend_account':
          return <Badge className={`${baseClass} bg-red-100 text-red-800 border-red-200`}>
            {getActionText(actionType)}
          </Badge>;
        case 'extend_trial':
          return <Badge className={`${baseClass} bg-yellow-100 text-yellow-800 border-yellow-200`}>
            {getActionText(actionType)}
          </Badge>;
        case 'payment_receipt_uploaded':
          return <Badge className={`${baseClass} bg-blue-100 text-blue-800 border-blue-200`}>
            {getActionText(actionType)}
          </Badge>;
        default:
          return <Badge variant="outline" className={baseClass}>
            {getActionText(actionType)}
          </Badge>;
      }
    };
  
    if (loading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      );
    }
  
    // NO hacemos return temprano si no hay logs, solo mostramos mensaje
    const showEmptyState = logs.length === 0;
  
    return (
      <div className="space-y-4">
        {/* Filtros y búsqueda - SIEMPRE visibles */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por admin, acción o detalles..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
  
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  {currentFilter === 'all' ? 'Todos' :
                   currentFilter === 'account' ? 'Cuentas' :
                   currentFilter === 'payment' ? 'Pagos' :
                   'Todos'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onFilterChange('all')}>
                  Todos los logs
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFilterChange('account')}>
                  <User className="h-4 w-4 mr-2" />
                  Acciones de cuentas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFilterChange('payment')}>
                  <Shield className="h-4 w-4 mr-2" />
                  Acciones de pagos
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
  
        {/* Estado vacío o tabla */}
        {showEmptyState ? (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No se encontraron logs
            </h3>
            <p className="text-gray-600 mb-4">
              {currentFilter !== 'all'
                ? `No hay logs con el filtro "${currentFilter}"`
                : 'No hay logs registrados en el sistema'}
            </p>
            <div className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  onSearch('');
                  setSearchQuery('');
                }}
              >
                Limpiar búsqueda
              </Button>
              <Button 
                variant="outline"
                onClick={() => onFilterChange('all')}
              >
                Ver todos
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Tabla */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha y Hora</TableHead>
                    <TableHead>Administrador</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Negocio</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 text-gray-400 mr-2" />
                          <div className="text-sm">
                            {formatDate(log.createdAt)}
                          </div>
                        </div>
                      </TableCell>
  
                      <TableCell>
                        <div className="flex items-center">
                          <User className="h-3 w-3 text-gray-400 mr-2" />
                          <div>
                            <div className="font-medium">{log.admin?.fullName || 'Sistema'}</div>
                            <div className="text-xs text-gray-500">@{log.admin?.username || 'system'}</div>
                          </div>
                        </div>
                      </TableCell>
  
                      <TableCell>
                        <div className="flex items-center">
                          {getActionIcon(log.actionType)}
                          <div>
                            {getActionBadge(log.actionType)}
                            {log.ipAddress && (
                              <div className="text-xs text-gray-500 mt-1">
                                IP: {log.ipAddress}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
  
                      <TableCell>
                        {log.shopId ? (
                          <div className="flex items-center">
                            <Store className="h-3 w-3 text-gray-400 mr-2" />
                            <div className="text-sm">
                              ID: {log.shopId.slice(0, 8)}...
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </TableCell>
  
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onViewDetails(log)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver detalles completos
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
  
            {/* Resumen - solo cuando hay resultados */}
            <div className="text-sm text-gray-600">
              Mostrando {logs.length} log{logs.length !== 1 ? 's' : ''}
              {currentFilter !== 'all' && ` con filtro "${currentFilter}"`}
            </div>
          </>
        )}
      </div>
    );
  }